import { queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { TopNav } from '../components/top-nav.js';
import {
  AdminForbiddenError,
  type AdminStrategistBrandRow,
  fetchAdminStrategistStatus,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { meQuery } from '../lib/queries.js';

// [12.2L-127] Admin strategist observability — /admin/strategist.
//
// One row per non-archived brand showing cadence decision, last 5
// proposals, lifetime spend. Built so we can tune the 14d/30d/60d
// cadence windows + 0.5 containment / 0.30 centroid thresholds
// against real-world brand behavior without SSH-ing into a SQL
// session.

const strategistStatusQuery = () =>
  queryOptions({
    queryKey: ['admin-strategist-status'] as const,
    queryFn: fetchAdminStrategistStatus,
    staleTime: 60_000,
    retry: false,
  });

export const Route = createFileRoute('/admin/strategist')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    try {
      await queryClient.ensureQueryData(strategistStatusQuery());
    } catch {
      // forbidden / network errors handled in-component
    }
  },
  component: AdminStrategistPage,
});

type SortKey = 'name' | 'spend' | 'totalProposals' | 'cadence';

function AdminStrategistPage() {
  useBrandTitle('admin', ['strategist']);
  const status = useQuery(strategistStatusQuery());
  const [sortKey, setSortKey] = useState<SortKey>('spend');
  const [skipFilter, setSkipFilter] = useState<string>('');

  const forbidden = status.error instanceof AdminForbiddenError;
  const brands = status.data ?? [];

  const filteredAndSorted = useMemo(() => {
    let rows = [...brands];
    if (skipFilter) {
      rows = rows.filter((b) => b.cadence.decision === 'skip' && b.cadence.reason === skipFilter);
    }
    rows.sort((a, b) => {
      if (sortKey === 'spend') return b.lifetimeSpendUsd - a.lifetimeSpendUsd;
      if (sortKey === 'totalProposals') return b.totalProposals - a.totalProposals;
      if (sortKey === 'cadence') {
        if (a.cadence.decision !== b.cadence.decision) {
          return a.cadence.decision === 'run' ? -1 : 1;
        }
      }
      return a.brandName.localeCompare(b.brandName);
    });
    return rows;
  }, [brands, sortKey, skipFilter]);

  const skipReasons = useMemo(() => {
    const reasons = new Set<string>();
    for (const b of brands) {
      if (b.cadence.decision === 'skip') reasons.add(b.cadence.reason);
    }
    return Array.from(reasons).sort();
  }, [brands]);

  const totals = useMemo(() => {
    let runCount = 0;
    let skipCount = 0;
    let totalSpend = 0;
    let totalProposals = 0;
    for (const b of brands) {
      if (b.cadence.decision === 'run') runCount += 1;
      else skipCount += 1;
      totalSpend += b.lifetimeSpendUsd;
      totalProposals += b.totalProposals;
    }
    return { runCount, skipCount, totalSpend, totalProposals };
  }, [brands]);

  return (
    <div className="min-h-dvh bg-[var(--paper)]">
      <TopNav active={null} />
      <main className="mx-auto max-w-[1280px] px-4 py-8 sm:px-6 sm:py-10">
        <header className="mb-7">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            ↘ admin · strategist
          </p>
          <h1 className="mt-1 font-display text-[28px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
            cross-brand strategist status.
          </h1>
          <p className="mt-2 text-[13px] text-[var(--ink)]/65">
            one row per non-archived brand. cadence reads the same module the brand-coach queue
            uses; spend is sum of strategist_cost_usd across every proposal ever.
          </p>
        </header>

        {forbidden ? (
          <Forbidden />
        ) : status.isLoading ? (
          <p className="font-mono text-[12px] text-[var(--ink)]/55">loading…</p>
        ) : brands.length === 0 ? (
          <p className="font-mono text-[12px] text-[var(--ink)]/55">no brands yet.</p>
        ) : (
          <>
            <TotalsRow totals={totals} />
            <Controls
              sortKey={sortKey}
              onSortChange={setSortKey}
              skipReasons={skipReasons}
              skipFilter={skipFilter}
              onSkipFilterChange={setSkipFilter}
            />
            <BrandTable brands={filteredAndSorted} />
          </>
        )}
      </main>
    </div>
  );
}

function TotalsRow({
  totals,
}: {
  totals: { runCount: number; skipCount: number; totalSpend: number; totalProposals: number };
}) {
  return (
    <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Stat label="run-eligible" value={String(totals.runCount)} />
      <Stat label="skipping" value={String(totals.skipCount)} />
      <Stat label="proposals ever" value={String(totals.totalProposals)} />
      <Stat label="lifetime cost" value={`$${totals.totalSpend.toFixed(2)}`} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-[var(--line)] bg-white px-3 py-2">
      <p className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
        {label}
      </p>
      <p className="mt-0.5 font-display text-[20px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
        {value}
      </p>
    </div>
  );
}

function Controls({
  sortKey,
  onSortChange,
  skipReasons,
  skipFilter,
  onSkipFilterChange,
}: {
  sortKey: SortKey;
  onSortChange: (k: SortKey) => void;
  skipReasons: string[];
  skipFilter: string;
  onSkipFilterChange: (r: string) => void;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-3">
      <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        sort
        <select
          value={sortKey}
          onChange={(e) => onSortChange(e.target.value as SortKey)}
          className="rounded-md border border-[var(--line)] bg-white px-2 py-1 text-[11.5px] text-[var(--ink)] focus:border-[var(--ink)] focus:outline-none"
        >
          <option value="spend">spend</option>
          <option value="totalProposals">total proposals</option>
          <option value="cadence">cadence (run first)</option>
          <option value="name">name</option>
        </select>
      </label>
      <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        filter by skip reason
        <select
          value={skipFilter}
          onChange={(e) => onSkipFilterChange(e.target.value)}
          className="rounded-md border border-[var(--line)] bg-white px-2 py-1 text-[11.5px] text-[var(--ink)] focus:border-[var(--ink)] focus:outline-none"
        >
          <option value="">all brands</option>
          {skipReasons.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function BrandTable({ brands }: { brands: AdminStrategistBrandRow[] }) {
  return (
    <div className="overflow-hidden rounded-2xl border-[1.5px] border-[var(--ink)] bg-white shadow-[4px_4px_0_var(--spell-soft)]">
      <table className="w-full table-fixed border-collapse text-[12px]">
        <thead className="bg-[var(--paper)]/60 text-left">
          <tr className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/65">
            <th className="w-[14%] px-3 py-2">brand</th>
            <th className="w-[18%] px-3 py-2">cadence</th>
            <th className="w-[6%] px-3 py-2 text-right">spend</th>
            <th className="w-[12%] px-3 py-2">lifecycle</th>
            <th className="w-[10%] px-3 py-2">bid cache</th>
            <th className="w-[12%] px-3 py-2">outcomes</th>
            <th className="w-[28%] px-3 py-2">last 5 proposals</th>
          </tr>
        </thead>
        <tbody>
          {brands.map((b) => (
            <BrandRow key={b.brandId} brand={b} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BrandRow({ brand }: { brand: AdminStrategistBrandRow }) {
  return (
    <tr className="border-t border-[var(--line)] align-top">
      <td className="px-3 py-3">
        <p className="font-display text-[13px] font-bold leading-tight text-[var(--ink)]">
          {brand.brandName}
        </p>
        <p className="mt-0.5 font-mono text-[10px] text-[var(--ink)]/45">{brand.brandSlug}</p>
      </td>
      <td className="px-3 py-3">
        <CadenceChip cadence={brand.cadence} />
      </td>
      <td className="px-3 py-3 text-right font-mono text-[12px] text-[var(--ink)]/85">
        ${brand.lifetimeSpendUsd.toFixed(4)}
      </td>
      <td className="px-3 py-3">
        <LifecycleChip lifecycle={brand.lifecycle} total={brand.totalProposals} />
      </td>
      <td className="px-3 py-3">
        <BidLookupChip bidLookup={brand.bidLookup} />
      </td>
      <td className="px-3 py-3">
        <OutcomesChip outcomes={brand.outcomes} />
      </td>
      <td className="px-3 py-3">
        <RecentProposals proposals={brand.recentProposals} />
      </td>
    </tr>
  );
}

function LifecycleChip({
  lifecycle,
  total,
}: {
  lifecycle: AdminStrategistBrandRow['lifecycle'];
  total: number;
}) {
  if (total === 0) {
    return (
      <span className="font-mono text-[10.5px] text-[var(--ink)]/40">— no proposals yet —</span>
    );
  }
  return (
    <div className="flex flex-col gap-0.5 font-mono text-[10.5px] text-[var(--ink)]/75">
      <span>
        {lifecycle.accepted > 0 ? `${lifecycle.accepted} accepted ` : ''}
        {lifecycle.dismissed > 0 ? `· ${lifecycle.dismissed} dismissed ` : ''}
        {lifecycle.abandoned > 0 ? (
          <span className="text-[var(--pulse)]/80">· {lifecycle.abandoned} abandoned</span>
        ) : null}
      </span>
      {lifecycle.open > 0 ? (
        <span className="text-[var(--ink)]/55">{lifecycle.open} open · founder hasn't acted</span>
      ) : null}
      <span className="text-[var(--ink)]/40">{total} total proposals</span>
    </div>
  );
}

function BidLookupChip({ bidLookup }: { bidLookup: AdminStrategistBrandRow['bidLookup'] }) {
  if (bidLookup.campaignsLaunched === 0) {
    return <span className="font-mono text-[10.5px] text-[var(--ink)]/40">— no launches —</span>;
  }
  const ratio =
    bidLookup.keywordsTotal > 0
      ? Math.round((bidLookup.keywordsMatched / bidLookup.keywordsTotal) * 100)
      : 0;
  const ratioTone =
    ratio >= 80
      ? 'text-[var(--sage)]'
      : ratio >= 40
        ? 'text-[var(--ink)]/85'
        : 'text-[var(--pulse)]';
  return (
    <div className="flex flex-col gap-0.5 font-mono text-[10.5px] text-[var(--ink)]/75">
      <span className={`font-semibold ${ratioTone}`}>{ratio}% kw matched</span>
      <span className="text-[var(--ink)]/55">
        {bidLookup.keywordsMatched}/{bidLookup.keywordsTotal} kw · {bidLookup.campaignsLaunched}{' '}
        {bidLookup.campaignsLaunched === 1 ? 'campaign' : 'campaigns'}
      </span>
    </div>
  );
}

function OutcomesChip({ outcomes }: { outcomes: AdminStrategistBrandRow['outcomes'] }) {
  if (outcomes.totalScored === 0) {
    return <span className="font-mono text-[10.5px] text-[var(--ink)]/40">— none scored —</span>;
  }
  const wins = outcomes.hit + outcomes.outperformed;
  const hitRate = ((wins / outcomes.totalScored) * 100).toFixed(0);
  return (
    <div className="flex flex-col gap-0.5 font-mono text-[10.5px] text-[var(--ink)]/75">
      <span className="font-semibold text-[var(--ink)]/90">
        {hitRate}% hit ({wins}/{outcomes.totalScored})
      </span>
      <span className="text-[var(--ink)]/55">
        {outcomes.hit > 0 ? `hit ${outcomes.hit} ` : ''}
        {outcomes.outperformed > 0 ? `+${outcomes.outperformed} ` : ''}
        {outcomes.miss_high_cost > 0 ? `· miss ${outcomes.miss_high_cost} ` : ''}
        {outcomes.dead > 0 ? `· dead ${outcomes.dead} ` : ''}
        {outcomes.inconclusive > 0 ? `· n/a ${outcomes.inconclusive}` : ''}
      </span>
    </div>
  );
}

function CadenceChip({ cadence }: { cadence: AdminStrategistBrandRow['cadence'] }) {
  if (cadence.decision === 'run') {
    return (
      <div>
        <span className="inline-flex items-center rounded-full bg-[var(--sage)]/15 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--sage)]">
          run · {cadence.reason}
        </span>
        <p className="mt-1 font-mono text-[10px] text-[var(--ink)]/45">
          window {cadence.effectiveWindowDays}d
        </p>
      </div>
    );
  }
  return (
    <div>
      <span className="inline-flex items-center rounded-full bg-[var(--ink)]/8 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/65">
        skip · {cadence.reason}
      </span>
      {cadence.detail ? (
        <p
          className="mt-1 line-clamp-2 font-mono text-[10px] text-[var(--ink)]/45"
          title={cadence.detail}
        >
          {cadence.detail}
        </p>
      ) : null}
    </div>
  );
}

function RecentProposals({ proposals }: { proposals: AdminStrategistBrandRow['recentProposals'] }) {
  if (proposals.length === 0) {
    return <span className="font-mono text-[10.5px] text-[var(--ink)]/40">— none —</span>;
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {proposals.map((p) => (
        <li
          key={p.id}
          className="flex items-baseline gap-2 font-mono text-[10.5px] text-[var(--ink)]/75"
        >
          <span
            className="inline-block min-w-[64px] shrink-0 rounded-sm bg-[var(--paper)]/60 px-1.5 py-0.5 text-center font-semibold uppercase tracking-[0.08em]"
            data-status={p.status}
          >
            {p.status}
          </span>
          <span className="flex-1 truncate" title={p.proposalName ?? '(unnamed)'}>
            {p.proposalName ?? '(unnamed)'}
          </span>
          <span className="text-[var(--ink)]/45" title={p.createdAt}>
            {relativeTime(p.createdAt)} · ${p.costUsd.toFixed(4)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function relativeTime(iso: string): string {
  const target = new Date(iso).getTime();
  const diffMs = Date.now() - target;
  if (diffMs < 0) return 'future?';
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function Forbidden() {
  return (
    <div className="rounded-xl border border-[var(--line)] bg-white p-6 text-[13px] text-[var(--ink)]/70">
      <p>this view is admin-only. your account isn't on the allow list.</p>
    </div>
  );
}
